import React, { useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import { EXCLAMATION_WITH_CIRCLE_ICON, FLEX_ICON, FLEX_SAVE_UP_TO_PERCENT } from '@constants';
import { ProductCode } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { isHsaFsaAvailable } from '@utils/flexHsaFsa';
import { useShoppingContext } from './ProductShoppingContext';

type Props = {
  productCode?: ProductCode | null;
  noMarginTop?: boolean;
};

const FlexPDPHoverOverlay: React.FC<Props> = ({ productCode, noMarginTop }) => {
  const shoppingData = useShoppingContext();
  const { selectedOptionCartItem, productId } = shoppingData;
  const { country } = useCountry();
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const productCodeFromContext = (selectedOptionCartItem?.id || productId) as ProductCode;

  if (!isHsaFsaAvailable(country, productCode || productCodeFromContext)) {
    return null;
  }

  return (
    <Wrapper $noMarginTop={noMarginTop}>
      <StaticWrapper>
        <ExclamationWithCircleIcon
          src={EXCLAMATION_WITH_CIRCLE_ICON}
          alt="Exclamation with circle"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => setIsHovered((prev) => !prev)}
        />
        <StaticText $variant={TextVariant.BodyRegularMD}>
          {t('components.flex-pdp-hover-static-text', { percent: FLEX_SAVE_UP_TO_PERCENT })}
        </StaticText>
        <FlexIcon src={FLEX_ICON} alt="Flex" />
      </StaticWrapper>
      <OverlayWrapper $isHovered={isHovered}>
        <OverlayHeading $variant={TextVariant.BodyBoldLG}>{t('components.flex-pdp-hover-overlay-heading')}</OverlayHeading>
        <OverlayText $variant={TextVariant.BodyRegularLG}>{t('components.flex-pdp-hover-overlay-text')}</OverlayText>
      </OverlayWrapper>
    </Wrapper>
  );
};

export default FlexPDPHoverOverlay;

const Wrapper = styled.div<{ $noMarginTop?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: ${({ $noMarginTop }) => ($noMarginTop ? '0' : '16px')};
  width: 100%;
`;

const OverlayWrapper = styled.div<{ $isHovered: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: absolute;
  top: 32px;
  left: 0;
  opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
  transition: opacity 0.4s ease-in-out;
  padding: 32px 24px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.1);
  z-index: 1;
  flex-direction: column;

  width: 100%;
`;

const StaticWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const ExclamationWithCircleIcon = styled.img`
  padding: 2px 0;
`;

const FlexIcon = styled.img`
  width: 23px;
  min-width: 23px;
`;

const StaticText = styled(Typography)`
  width: 16px;
  min-width: 16px;
  width: 100%;
  line-height: 1;
`;

const OverlayHeading = styled(Typography)`
  color: #484a53;
  font-size: 24px;
  text-align: center;
`;

const OverlayText = styled(Typography)`
  color: #9c9c9c;
  text-align: center;
`;
